<template>
    <div :class="['container', { contained }]" :style="styles">
        <div :id="elementHandle" :class="['owl-carousel', { contained }]">
            <slot></slot>
        </div>
    </div>
</template>

<script lang="ts">
/// <reference types="jquery" />
/// <reference types="owl.carousel" />
import Vue from 'vue';
import '@/common/css/owl.carousel.min.css';
import '@/common/css/owl.theme.default.css';
import { Optimization } from '@reolink-web/utils';
import { mapGetters } from 'vuex';

export default Vue.extend({
    data () {

        const random = (): string => ( Math.random().toString( 36 ).substring( 2, 15 ) + Math.random().toString( 36 ).substring( 2, 15 ));
        return {

            elementHandle: 'carousel_' + random(),
            resizeObserver: null as ResizeObserver | null,
            containerResizeObserver: null as ResizeObserver | null,
            lastConfig: null as OwlCarousel.Options | null

        };

    },
    computed:{
        ...mapGetters( ['isMobile'] as const ),
        styles(): Record<string, string> {

            return {

                minHeight: this.minHeight + 'px'

            };

        }
    },
    props: {

        items: {

            type: Number,
            default: 3

        },
        mouseDrag: {

            type: Boolean,
            default: true

        },
        minHeight: {

            type: Number,
            default: 700

        },
        loop: Boolean,
        dots: Boolean,
        contained: Boolean,
        nav: {
            type: Boolean,
            default: true
        },
        stagePadding: {
            type: Number,
            default: 0
        },
        autoWidth: {
            type: Boolean,
            default: false
        }

    },
    mounted () {

        // 直接覆盖 init 方法为防抖版本
        this.init = Optimization.debounce( this.init.bind( this ), 100 );

        this.$emit( 'done' );
        this.init();

    },
    beforeDestroy() {

        if ( this.resizeObserver ) {

            this.resizeObserver.disconnect();

        }

        if ( this.containerResizeObserver ) {

            this.containerResizeObserver.disconnect();

        }

    },
    methods: {

        init(): void {

            const carousel = window.jquerySelf( `#${this.elementHandle}` );
            const config = this.calculateConfig();

            // 如果配置没有变化，不需要重新初始化
            if ( this.lastConfig && this.configEquals( this.lastConfig, config )) {

                return;

            }

            carousel.trigger( 'destroy.owl.carousel' );
            carousel.one( 'refreshed.owl.carousel', () => {

                this.$emit( 'ready' );

            });
            carousel.owlCarousel( config );
            this.lastConfig = { ...config };

            // 监听 owl-stage 高度变化和容器宽度变化（只设置一次）
            this.$nextTick(() => {

                this.setupResizeObservers();

            });

        },

        setupResizeObservers(): void {

            if ( this.resizeObserver ) {

                this.resizeObserver.disconnect();

            }
            if ( this.containerResizeObserver ) {

                this.containerResizeObserver.disconnect();

            }

            const owlStage = document.querySelector( `#${this.elementHandle} .owl-stage` );
            if ( owlStage ) {

                this.resizeObserver = new ResizeObserver(() => {

                    this.update();

                });
                this.resizeObserver.observe( owlStage );

            }

            const container = this.$el;
            if ( container ) {

                this.containerResizeObserver = new ResizeObserver(() => {

                    this.init();

                });
                this.containerResizeObserver.observe( container );

            }

        },

        configEquals( config1: OwlCarousel.Options, config2: OwlCarousel.Options ): boolean {

            return config1.items === config2.items &&
                   config1.autoWidth === config2.autoWidth &&
                   Math.abs(( config1.stagePadding ?? 0 ) - ( config2.stagePadding ?? 0 )) < 1;

        },

        calculateConfig(): OwlCarousel.Options {

            if ( this.autoWidth ) {

                return {
                    items: this.items,
                    autoWidth: true,
                    mouseDrag: this.mouseDrag,
                    nav: this.nav,
                    margin: 32,
                    loop: this.loop,
                    dots: this.dots,
                    navText: ['<span class="reo-nav-handle icon-icon_arrow-left"></span>', '<span class="reo-nav-handle icon-icon_arrow-right"></span>'],
                };

            }

            const containerWidth = this.$el.clientWidth;
            const itemWidth = 484; // 每个item的最大宽度
            const margin = this.isMobile ? 8 : 32; // 间距
            const minItemWidth = 40; // 第二个item的最小显示宽度

            let items = 1;
            let stagePadding = 0;

            // 如果容器宽度 >= 1000px，显示2个完整item
            if ( containerWidth >= 1000 || !this.isMobile ) {

                items = 2;
                stagePadding = 0;

            } else if ( this.stagePadding > 0 ) {

                stagePadding = this.stagePadding;

            } else {

                // 计算第二个item可以露出多少
                // 由于stagePadding会在左右都加，所以我们除以2来只在右边显示
                const availableWidth = containerWidth - itemWidth - margin;

                if ( availableWidth >= minItemWidth ) {

                    stagePadding = Math.min( availableWidth, itemWidth ) / 2;

                } else if ( availableWidth > 0 ) {

                    stagePadding = availableWidth / 2;

                } else {

                    stagePadding = 16;

                }

            }

            return {
                items: items,
                mouseDrag: this.mouseDrag,
                nav: this.nav,
                margin: margin,
                stagePadding: stagePadding,
                center: false,
                loop: this.loop,
                navText: ['<span class="reo-nav-handle icon-icon_arrow-left"></span>', '<span class="reo-nav-handle icon-icon_arrow-right"></span>'],
                dots: this.dots
            };

        },
        update(): void {

            this.$nextTick(() => {

                $( `#${this.elementHandle}` ).trigger( 'refresh.owl.carousel' );

            });

        }

    }

});

</script>

<style lang="less" scoped>
.container{
    .mobile({
        width: 100vw;
        position: relative;
        left: 50%;
        transform: translateX(-50%);
        display: flex;
        justify-content: center;
    });
    &.contained{
        .mobile({
            width: 100%;
            left: auto;
            transform: none;
        });
    }
}
body  .owl-carousel {
    .mobile({
        width: calc(100% - 40px);
        max-width: 1000px;
    });
    &.contained{
        .mobile({
            width: 100%;
            max-width: 100%;
        });
    }
    /deep/ .owl-stage{
        display: flex;
        padding: 0 !important;
    }

    /deep/ .owl-nav{
        &>div{
            display: inline-block;
            padding: 10px 0;
            position: absolute;
            transform: translateY(-50%);
            top: 50%;
            padding: 0;
            width: 0.5rem;
            height: 0.5rem;
            border-radius: 100%;
            background-color: #fff;
            opacity: @carousel-nav-opacity;
            box-shadow: @carousel-nav-box-shadow;
            span{
                font-size: 0.16rem;
                position: absolute;
                left: 50%;
                top: 50%;
                transform: translate(-50%, -50%);
            }
            &:not(.disabled):hover{
                opacity: 1;
                span{
                    color:@brand_light;
                }
            }
        }
        .owl-prev{
            left: -.25rem;
            background-color: @carousel-nav-background;
            &.disabled{
                display: none;
            }
        }
        .owl-next{
            right: -.25rem;
            background-color: @carousel-nav-background;
            &.disabled{
                display: none;
            }
        }
    }

    /deep/ .owl-item:last-of-type{
        margin-right: 0 !important;
    }
    /deep/ .owl-stage-outer{
        padding-bottom: 1px;
    }

}
</style>
